import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { FiscalYearContext } from "../FiscalYear/FiscalYearContext";
import getCompanyPack from "../Auth/GetCompanyPack";
import authHeader from "../Auth/authHeader";
import logo from "../Images/only_logo.png";
import text from "../Images/only_slogan.png";
import { getMenuEngineSidebar } from "./menuEngineApi";
import { getMenuCacheIdentity, readMenuCache, writeMenuCache } from "./menuSidebarCache";
import "./NewSidebar.scss";
import "./DatabaseSidebarPreview.scss";
import LockedMenuPreview from "./LockedMenuPreview";
import { Tooltip } from "@mui/material";
import { getRequiredPackage } from "./menuPackagePresentation";

const createPageUrl = (path) => path;

function normalizeNode(node) {
  const menuID = node?.MenuID ?? node?.menuID;
  const children = node?.Children ?? node?.children ?? [];

  return {
    id: menuID,
    name: node?.Title ?? node?.title ?? "",
    to: node?.Path ?? node?.path ?? "",
    isLocked: node?.IsLocked ?? node?.isLocked ?? false,
    availablePackages: node?.AvailablePackages ?? node?.availablePackages ?? "",
    iconClassName: node?.Icon ?? node?.icon ?? "bi bi-dot",
    children: Array.isArray(children) ? children.map(normalizeNode) : [],
  };
}

function removePath(nodes, path) {
  return (nodes || [])
    .filter(node => node.to !== path)
    .map(node => ({ ...node, children: removePath(node.children, path) }));
}

function prepareMenuTree(data) {
  const normalizedTree = removePath(data.map(normalizeNode), "/support");
  let configurationMenu = findConfigurationMenu(normalizedTree);
  if (!configurationMenu) {
    configurationMenu = {
      id: "support-configuration",
      name: "Konfigurimet",
      to: "#",
      isLocked: false,
      availablePackages: "",
      iconClassName: "bi bi-gear-wide-connected",
      children: [],
    };
    normalizedTree.push(configurationMenu);
  }
  configurationMenu.children.push({
    id: "support",
    name: "Supporti",
    to: "/support",
    isLocked: false,
    availablePackages: "",
    iconClassName: "bi bi-headset",
    children: [],
  });
  return normalizedTree;
}

function findConfigurationMenu(nodes) {
  const direct = (nodes || []).find(node =>
    String(node.name || "").toLowerCase().includes("konfigur"));
  if (direct) return direct;

  return (nodes || []).find(node =>
    (node.children || []).filter(child => String(child.to || "").startsWith("/configuration/")).length >= 2);
}

function filterTree(nodes, searchText) {
  if (!searchText) {
    return nodes;
  }

  const normalized = searchText.toLowerCase();

  return nodes
    .map((node) => {
      const filteredChildren = filterTree(node.children || [], normalized);
      const selfMatch = (node.name || "").toLowerCase().includes(normalized);

      if (selfMatch || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
          hasMatchingSubMenus: filteredChildren.length > 0,
        };
      }

      return null;
    })
    .filter(Boolean);
}

export default function DatabaseSidebar({
  isOpen,
  isMobile,
  onCloseMobile,
  onExpand,
  searchTerm = "",
  onFatalError,
  onMenuReady,
  retryKey = 0,
}) {
  const { pathname } = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [searchMenuOverrides, setSearchMenuOverrides] = useState({});
  const previousHasSearchRef = useRef(false);
  const [pack, setPack] = useState("");
  const [version, setVersion] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetch("/version.json", { cache: "no-store", signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error("Version could not be loaded");
        return response.json();
      })
      .then(data => setVersion(typeof data.version === "string" ? data.version.trim() : ""))
      .catch(() => {});
    return () => controller.abort();
  }, []);
  const [menuItems, setMenuItems] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [previewMenu, setPreviewMenu] = useState(null);

  const { fiscalYear, setFiscalYear } = useContext(FiscalYearContext);

  const headers = authHeader();

  useEffect(() => {
    getCompanyPack(headers).then(setPack);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadSidebar = async () => {
      let hasCachedMenu = false;
      try {
        setLoadingMenu(true);
        const identity = await getMenuCacheIdentity(headers.Authorization).catch(() => null);
        if (!mounted) return;

        const cachedData = readMenuCache(identity);
        if (cachedData) {
          setMenuItems(prepareMenuTree(cachedData));
          setLoadingMenu(false);
          hasCachedMenu = true;
          if (onMenuReady) onMenuReady();
        }

        const data = await getMenuEngineSidebar(headers, { includeLocked: true });
        if (!mounted) {
          return;
        }
        if (!Array.isArray(data) || data.length === 0) {
          throw new Error("Menuja nga databaza është bosh ose e pavlefshme.");
        }

        setMenuItems(prepareMenuTree(data));
        writeMenuCache(identity, data);
        if (!hasCachedMenu && onMenuReady) onMenuReady();
      } catch (error) {
        if (mounted && !hasCachedMenu && onFatalError) {
          onFatalError(error);
        }
      } finally {
        if (mounted) {
          setLoadingMenu(false);
        }
      }
    };

    loadSidebar();

    return () => {
      mounted = false;
    };
  }, [retryKey]);

  const handleFiscalYearChange = (event) => {
    setFiscalYear(Number(event.target.value));
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const hasSearch = normalizedSearch.length > 0;

  useEffect(() => {
    if (!isOpen) {
      setOpenMenus({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!hasSearch) {
      setSearchMenuOverrides({});
    }
  }, [hasSearch]);

  useEffect(() => {
    const wasSearching = previousHasSearchRef.current;

    if (!wasSearching && hasSearch && !isOpen && onExpand) {
      onExpand();
    }

    previousHasSearchRef.current = hasSearch;
  }, [hasSearch, isOpen, onExpand]);

  const sidebarWidth = isOpen ? "sidebar-expanded" : "sidebar-collapsed";
  const mobileClasses = isMobile
    ? `sidebar-mobile ${isOpen ? "sidebar-mobile-open" : "sidebar-mobile-closed"}`
    : `sidebar-desktop ${sidebarWidth} sidebar-hover-expand`;

  const overlay = isMobile && isOpen ? (
    <div className="sidebar-overlay" onClick={onCloseMobile} />
  ) : null;

  const toggleMenu = (id, currentExpanded) => {
    if (hasSearch) {
      setSearchMenuOverrides((prev) => ({
        ...prev,
        [id]: !currentExpanded,
      }));
      return;
    }

    setOpenMenus((prev) => {
      if (prev[id]) {
        return {};
      }

      if (!isOpen && onExpand) {
        onExpand();
      }

      return { [id]: true };
    });
  };

  const filteredMenuItems = filterTree(menuItems, normalizedSearch);

  const renderMenu = (items) =>
    items.map((item) => {
      const hasSubMenus = Array.isArray(item.children) && item.children.length > 0;

      if (hasSubMenus) {
        const isExpanded = hasSearch
          ? searchMenuOverrides[item.id] ?? item.hasMatchingSubMenus
          : openMenus[item.id];

        const lockedLeaves = [];
        const allChildrenLocked = (node) => {
          if (node.children?.length) return node.children.every(allChildrenLocked);
          if (node.isLocked) lockedLeaves.push(node);
          return node.isLocked;
        };
        const groupLocked = item.children.every(allChildrenLocked);
        const groupPackage = groupLocked
          ? getRequiredPackage(lockedLeaves.map(node => node.availablePackages).join(","), pack)
          : null;

        const isChildActive = item.children.some((child) => {
          const childUrl = createPageUrl(child.to || "");
          return childUrl && (pathname === childUrl || pathname.startsWith(childUrl + "/"));
        });

        return (
          <div key={item.id || item.name} className="sidebar-menu-group">
            <div className="sidebar-menu-items">
              <div className="sidebar-menu-item-wrapper">
                <button
                  type="button"
                  aria-label={item.name}
                  aria-expanded={Boolean(isExpanded)}
                  onClick={() => toggleMenu(item.id || item.name, isExpanded)}
                  className={`sidebar-menu-item sidebar-collapse-btn ${
                    isChildActive || isExpanded ? "sidebar-active" : "sidebar-inactive"
                  }`}
                >
                  <div className="sidebar-item-left">
                    <i className={item.iconClassName + " sidebar-icon"} />
                    <span className="sidebar-item-text">{item.name}</span>
                  </div>
                  {groupLocked && <Tooltip title={groupPackage.name ? `Zbuloni funksionet e këtij grupi me pakon ${groupPackage.name} ose më lart.` : "Zbuloni funksionet e këtij grupi me një pako tjetër."} arrow enterTouchDelay={0}>
                    <span className="db-sidebar-lock" style={{ color: groupPackage.color, marginRight: 8 }}><groupPackage.Icon size={14} aria-hidden="true" /></span>
                  </Tooltip>}
                  <div className="sidebar-item-arrow">
                    {isExpanded ? (
                      <ChevronDown className="sidebar-arrow-icon" />
                    ) : (
                      <ChevronRight className="sidebar-arrow-icon" />
                    )}
                  </div>
                </button>

                {isExpanded && <div className="sidebar-submenu">{renderMenu(item.children)}</div>}
              </div>
            </div>
          </div>
        );
      }

      if (!item.to) {
        return null;
      }

      if (item.isLocked) {
        const requiredPackage = getRequiredPackage(item.availablePackages, pack);
        const lockMessage = requiredPackage.name
          ? `Për ta përdorur, aktivizoni pakon ${requiredPackage.name} ose një pako tjetër që e përfshin këtë funksion.`
          : "Për ta përdorur, aktivizoni një pako që e përfshin këtë funksion.";
        return (
          <div key={item.id || item.name} className="sidebar-menu-group">
            <div className="sidebar-menu-items">
              <div className="sidebar-menu-item-wrapper">
                <button type="button" className="sidebar-menu-item sidebar-collapse-btn sidebar-inactive db-sidebar-locked"
                  aria-label={`${item.name} — shiko mundësitë e pakos`}
                  onClick={() => {
                    setPreviewMenu(item);
                    if (isMobile && onCloseMobile) onCloseMobile();
                  }}>
                  <div className="sidebar-item-left">
                    <i className={item.iconClassName + " sidebar-icon"} aria-hidden="true" />
                    <span className="sidebar-item-text">{item.name}</span>
                  </div>
                  <Tooltip title={lockMessage} arrow enterTouchDelay={0}>
                    <span className="db-sidebar-lock" style={{ color: requiredPackage.color }}>
                      <requiredPackage.Icon size={14} aria-hidden="true" />
                    </span>
                  </Tooltip>
                </button>
              </div>
            </div>
          </div>
        );
      }

      const url = createPageUrl(item.to);
      const isActive = pathname === url || pathname.startsWith(url + "/");

      return (
        <div key={item.id || item.name} className="sidebar-menu-group">
          <div className="sidebar-menu-items">
            <div className="sidebar-menu-item-wrapper">
              <Link
                to={url}
                className={`sidebar-menu-item sidebar-link ${
                  isActive ? "sidebar-active" : "sidebar-inactive"
                }`}
              >
                {isActive && <div className="sidebar-active-indicator" />}
                <div className="sidebar-item-left">
                  <i className={item.iconClassName + " sidebar-icon"} />
                  <span className="sidebar-item-text">{item.name}</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      );
    });

  return (
    <>
      {previewMenu && <LockedMenuPreview key={previewMenu.id} menu={previewMenu} currentPackage={pack} onClose={() => setPreviewMenu(null)} />}
      {overlay}
      <aside className={`db-sidebar-preview ${mobileClasses}`}>
        {isMobile && (
          <div className="sidebar-mobile-header">
            <Link to="/dashboard" className="sidebar-brand-link sidebar-brand-link-mobile">
              <img src={logo} alt="Accountree logo" className="sidebar-brand-logo" />
              <img src={text} alt="Accountree" className="sidebar-brand-text sidebar-brand-text-mobile" />
            </Link>
          </div>
        )}

        {!isMobile && (
          <div className="sidebar-desktop-header">
            <Link to="/dashboard" className="sidebar-brand-link">
              <img src={logo} alt="Accountree logo" className="sidebar-brand-logo" />
              <img src={text} alt="Accountree" className="sidebar-brand-text" />
            </Link>
          </div>
        )}

        <div className="sidebar-menu-container">
          {loadingMenu ? <div className="sidebar-loading">Loading menu...</div> : renderMenu(filteredMenuItems)}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-footer-content">
            <div className="sidebar-fiscal-year">
              <label>Fiscal Year</label>
              <select className="sidebar-select" value={fiscalYear} onChange={handleFiscalYearChange}>
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
                <option value={2024}>2024</option>
                <option value={2023}>2023</option>
                <option value={2022}>2022</option>
              </select>
            </div>

            <div className="sidebar-package-info">
              <div className="sidebar-badge">
                <span style={{ marginRight: "4px" }}>*</span>
                Package: {pack || "Loading..."}
              </div>
              <div className="sidebar-copyright">
                <p>© {new Date().getFullYear()} Accountree{version ? ` | Version: v${version}` : ""}</p>
              </div>
            </div>
          </div>

          {!isMobile && !isOpen && (
            <div className="sidebar-collapsed-badge">
              <div className="sidebar-mini-badge" title={`Package: ${pack || "Loading..."} | Fiscal Year ${fiscalYear}-${fiscalYear + 1}`}>
                {pack ? pack.charAt(0) : "?"}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
